/**
 * FINAL REVIEW PAGE (Step 11)
 * 
 * Purpose: Review all generated documents before mailing
 * Features:
 * - Summary of all generated documents
 * - Download Full Package button
 * - Save Progress button
 * - Prompt to add client to Quick Reminder with auto-filled data
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CheckCircle2, FileText, Download, Save, DollarSign, Mail, 
  ArrowRight, AlertCircle, Package, Users, Loader2, X,
  ClipboardCheck, Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import PromisedPaymentsPanel from './PromisedPaymentsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinalReviewPage({
  clientId,
  clientData,
  signatureData,
  selectedAccounts = [],
  selectedInquiries = [],
  ftcReportGenerated,
  ftcTheme,
  hasBankruptcy,
  bankruptcyLetterGenerated,
  idDocumentUploaded,
  ssnCardUploaded,
  disputeLettersGenerated,
  inquiryLettersGenerated,
  supplementalLettersGenerated,
  onComplete,
  onNavigateToStep
}) {
  const [downloading, setDownloading] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [showQuickReminderModal, setShowQuickReminderModal] = useState(false);
  const [addingToReminder, setAddingToReminder] = useState(false);
  const [reminderBalance, setReminderBalance] = useState('');
  
  // Generate summary of all documents
  const documentSummary = [
    { 
      name: 'Dispute Letters', 
      generated: disputeLettersGenerated, 
      count: selectedAccounts.length,
      step: 5 
    },
    { 
      name: 'Inquiry Removal Letters', 
      generated: inquiryLettersGenerated, 
      count: selectedInquiries.length,
      step: 7 
    },
    { 
      name: 'FTC Identity Theft Report', 
      generated: ftcReportGenerated, 
      count: ftcReportGenerated ? 1 : 0,
      step: 8 
    },
    { 
      name: 'Bankruptcy Dispute Letters', 
      generated: bankruptcyLetterGenerated, 
      count: bankruptcyLetterGenerated ? 3 : 0, // One per bureau
      step: 9 
    },
    { 
      name: 'ID Document', 
      generated: idDocumentUploaded, 
      count: idDocumentUploaded ? 1 : 0,
      step: 10 
    },
    { 
      name: 'Social Security Card', 
      generated: ssnCardUploaded, 
      count: ssnCardUploaded ? 1 : 0,
      step: 10 
    }
  ].filter(doc => doc.generated || doc.count > 0);

  const totalDocuments = documentSummary.reduce((sum, doc) => sum + doc.count, 0);

  // Download full package
  const handleDownloadPackage = async () => {
    if (!clientId) {
      toast.error('No client selected');
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/api/documents/download-all/${clientId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const clientName = clientData?.name?.replace(/\s+/g, '_') || 'Client';
      link.download = `${clientName}_Dispute_Package.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Full dispute package downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download package');
    } finally {
      setDownloading(false);
    }
  };

  // Save progress
  const handleSaveProgress = async () => {
    if (!clientId) {
      toast.error('No client selected');
      return;
    }
    
    setSavingProgress(true);
    try {
      // Save current wizard state to localStorage
      localStorage.setItem(`wizard_step_${clientId}`, '11');
      localStorage.setItem(`wizard_progress_${clientId}`, JSON.stringify({
        accounts_disputed: selectedAccounts.length,
        inquiries_disputed: selectedInquiries.length,
        ftc_generated: ftcReportGenerated,
        bankruptcy_disputed: hasBankruptcy && bankruptcyLetterGenerated,
        saved_at: new Date().toISOString()
      }));
      
      toast.success('Progress saved! You can return to this client anytime.');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save progress');
    } finally {
      setSavingProgress(false);
    }
  };

  // Add client to Quick Reminder
  const handleAddToQuickReminder = async () => {
    if (!clientData?.name) {
      toast.error('Client name is required');
      return;
    }
    
    if (!reminderBalance || parseFloat(reminderBalance) <= 0) {
      toast.error('Please enter a valid balance amount');
      return;
    }
    
    setAddingToReminder(true);
    try {
      const response = await axios.post(`${API}/api/quick-reminder/send`, {
        clientName: clientData.name,
        clientEmail: clientData.email || null,
        clientPhone: clientData.phone || null,
        balance: reminderBalance,
        sendMethod: 'email'
      });
      
      if (response.data.success) {
        toast.success(`${clientData.name} added to Quick Reminder!`);
        setShowQuickReminderModal(false);
        setReminderBalance('');
      } else {
        toast.error(response.data.error || 'Failed to add to Quick Reminder');
      }
    } catch (error) {
      console.error('Quick Reminder error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to Quick Reminder');
    } finally {
      setAddingToReminder(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-3">
          <ClipboardCheck className="w-10 h-10 text-green-400" />
          <h2 className="text-3xl font-bold text-white">Final Review</h2>
        </div>
        <p className="text-zinc-400">
          Review your dispute package before mailing to bureaus
        </p>
      </div>

      {/* Client Info Card */}
      <Card className="border-zinc-700 bg-zinc-900/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-blue-400" />
            Client Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">Name:</span>
              <span className="ml-2 text-white font-medium">{clientData?.name || 'N/A'}</span>
            </div>
            <div>
              <span className="text-zinc-500">Email:</span>
              <span className="ml-2 text-white">{clientData?.email || 'N/A'}</span>
            </div>
            <div>
              <span className="text-zinc-500">Phone:</span>
              <span className="ml-2 text-white">{clientData?.phone || 'N/A'}</span>
            </div>
            <div>
              <span className="text-zinc-500">Address:</span>
              <span className="ml-2 text-white">
                {clientData?.address ? `${clientData.address}, ${clientData.city || ''} ${clientData.state || ''} ${clientData.zip_code || ''}`.trim() : 'N/A'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Summary */}
      <Card className="border-zinc-700 bg-zinc-900/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-purple-400" />
              Document Summary
            </div>
            <Badge className="bg-green-600 text-white">
              {totalDocuments} Documents Ready
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {documentSummary.map((doc, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700"
              >
                <div className="flex items-center gap-3">
                  {doc.generated ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  )}
                  <span className="text-white">{doc.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                    {doc.count} {doc.count === 1 ? 'document' : 'documents'}
                  </Badge>
                  {!doc.generated && doc.step && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => onNavigateToStep(doc.step)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      Go to Step {doc.step}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Download Full Package */}
        <Button
          onClick={handleDownloadPackage}
          disabled={downloading || totalDocuments === 0}
          className="h-20 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white flex flex-col items-center justify-center gap-2"
          data-testid="download-package-btn"
        >
          {downloading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Download className="w-6 h-6" />
          )}
          <span className="font-semibold">Download Full Package</span>
        </Button>

        {/* Save Progress */}
        <Button
          onClick={handleSaveProgress}
          disabled={savingProgress}
          className="h-20 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white flex flex-col items-center justify-center gap-2"
          data-testid="save-progress-btn"
        >
          {savingProgress ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Save className="w-6 h-6" />
          )}
          <span className="font-semibold">Save Progress</span>
        </Button>

        {/* Add to Quick Reminder */}
        <Button
          onClick={() => setShowQuickReminderModal(true)}
          className="h-20 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white flex flex-col items-center justify-center gap-2"
          data-testid="add-to-reminder-btn"
        >
          <DollarSign className="w-6 h-6" />
          <span className="font-semibold">Add to Quick Reminder</span>
        </Button>
      </div>

      {/* Payment Plan — promised dates (up to 6) OR weekly recurring. Reminders
          fire at 9 AM Pacific to the client AND clearallcreditrepair@gmail.com. */}
      {clientData?.name && (
        <div className="mt-4">
          <PromisedPaymentsPanel
            clientName={clientData.name}
            clientEmail={clientData.email || ""}
            defaultAmount={reminderBalance}
            source="ftc_report"
            sourceId={clientId}
          />
        </div>
      )}

      {/* Continue to Mailing */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={onComplete}
          className="px-8 py-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg font-bold flex items-center gap-3"
          data-testid="continue-to-mailing-btn"
        >
          <Mail className="w-6 h-6" />
          Continue to Mailing
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Quick Reminder Modal */}
      <Dialog open={showQuickReminderModal} onOpenChange={setShowQuickReminderModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5 text-orange-400" />
              Add to Quick Reminder
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Track payment balance for {clientData?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Auto-filled Client Info */}
            <div className="bg-zinc-800/50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Client Name:</span>
                <span className="text-white font-medium">{clientData?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Email:</span>
                <span className="text-white">{clientData?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Phone:</span>
                <span className="text-white">{clientData?.phone || 'N/A'}</span>
              </div>
            </div>

            {/* Balance Input */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Balance Owed *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="number"
                  value={reminderBalance}
                  onChange={(e) => setReminderBalance(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-lg font-medium focus:border-orange-500 focus:outline-none"
                  data-testid="reminder-balance-input"
                />
              </div>
            </div>

            {/* Payment Methods Preview */}
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-xs">
              <p className="text-zinc-400 mb-2 font-medium">Payment Methods (included in email):</p>
              <div className="text-zinc-500 grid grid-cols-2 gap-1">
                <span>Zelle: srsworldLLC@gmail.com</span>
                <span>Venmo: Clearall-Creditrepairs-1</span>
                <span>Apple Pay: 619-253-9785</span>
                <span>PayPal/Cash App: +$3/100</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowQuickReminderModal(false)}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToQuickReminder}
              disabled={addingToReminder || !reminderBalance}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-add-reminder-btn"
            >
              {addingToReminder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Add & Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
